import React, { useState, useRef, useCallback } from 'react'
import { Collection } from '@shopify/hydrogen/storefront-api-types'
import { CollectionCard } from './collection-card.client'
import styles from '../../assets/scss/components/collections/collections-swiper.module.scss'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import 'swiper/scss'
import 'swiper/scss/navigation'
import type { EnhancedMenuItem } from '../../lib/menus'
import { useI18n } from '../i18n.client'

interface CollectionSwiperProps {
  collections: Collection[]
  collectionsMenu?: EnhancedMenuItem
}

const CollectionSwiper: React.FC<CollectionSwiperProps> = ({ collections, collectionsMenu }) => {
  if (!collectionsMenu) {
    return null
  }

  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null)
  const navigationPrevRef = useRef<HTMLDivElement>(null)
  const navigationNextRef = useRef<HTMLDivElement>(null)
  const [i18n] = useI18n()

  const handlePrevClick = useCallback(() => {
    if (swiperRef) {
      swiperRef.slidePrev()
    }
  }, [swiperRef])

  const handleNextClick = useCallback(() => {
    if (swiperRef) {
      swiperRef.slideNext()
    }
  }, [swiperRef])

  return (
    <div>
      <div className={styles.swiperButton}>
        <div className={styles.navigationPrev} onClick={handlePrevClick} ref={navigationPrevRef}>
          <img src="/carousel-arrow.svg" alt="Previous" width={27} height={8} />
        </div>
        <div className={styles.eyeIconWrapper}>
          <div className={styles.eyeIcon}>
            <img src="/eye-hover-icon.svg" alt="Eye Hover" />
          </div>
          <span>{i18n.translate('see-all-collections')}</span>
        </div>
        <div className={styles.navigationNext} onClick={handleNextClick} ref={navigationNextRef}>
          <img src="/carousel-arrow.svg" alt="Next" width={27} height={8} />
        </div>
      </div>
      <Swiper
        className={styles.swiper}
        freeMode
        modules={[FreeMode, Navigation]}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onSwiper={setSwiperRef}
        slidesPerGroup={1}
        spaceBetween={20}
        breakpoints={{
          0: {
            slidesPerView: 1.5,
            spaceBetween: 10,
          },
          650: {
            slidesPerView: 2.25,
            spaceBetween: 15,
          },
          890: {
            slidesPerView: 4,
            spaceBetween: 20,
          },
          1440: {
            slidesPerView: 5,
            spaceBetween: 20,
          },
        }}>
        {collectionsMenu.items.map(({ to }: { to: string }) => {
          const collection = collections.find(({ handle }) => `/collections/${handle}` === to)
          if (!collection) return null
          return (
            <SwiperSlide key={collection.id} className={styles.swiperSlide}>
              <CollectionCard collection={collection} />
            </SwiperSlide>
          )
        })}
      </Swiper>
      </div>
  )
}

export default CollectionSwiper
